/**
 * V2MediaSection — V2-native editor section for step.content.featuredMedia
 *
 * Edits: media kind (Image/Video), image/clip URL, enable/disable
 * V2 paths: content.featuredMedia.*
 *
 * Replaces: OnboardingMediaComponentSection (for V2 context)
 *
 * Sprint: schema-v2-migration, Sprint 4
 */

import React, { useCallback, useState, useMemo } from 'react';
import type { SelectChangeEvent } from '@mui/material';
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  Typography,
  Divider,
  Slider,
  Collapse,
  IconButton,
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import { SAccordion } from '@modules/ui-components';
import { StyleFieldRow } from '../../shared/styleFields';
import { ImageUploadElement } from '../../../factories/elements/ImageUploadElement';
import { VideoUploadElement } from '../../../factories/elements/VideoUploadElement';
import { PaddingElement, type PaddingValues } from '../../../factories/elements/PaddingElement';
import { EditorGuideProvider } from '../../../contexts';
import { OnboardingMediaUploadService } from '@modules/firebase-core/services/OnboardingMediaUploadService';
import {
  V2_MEDIA_KIND_OPTIONS,
  V2_DEFAULT_MEDIA_STRUCTURE,
  V2_DEFAULT_IMAGE_CONTENT,
  V2_DEFAULT_VIDEO_CONTENT,
  type V2MediaKind,
} from '../../../config/v2MediaConfig';
import {
  V2_FLEXIBLE_GRADIENT_STRUCTURE,
  V2_FLEXIBLE_GRADIENT_COLOR_CONFIG,
  V2_MEDIA_APPEARANCE_FIELDS,
  V2_MEDIA_TOP_ALIGNMENT_FIELD,
  V2_MEDIA_REPEAT_FIELD,
} from '../../../config/v2FlexibleMediaConfig';
import { withScreenEditorFieldGuide, withScreenEditorFieldGuides } from '../../../config/screenEditorFieldGuideResolver';
import { SECTION_MARGIN_BOTTOM, FIELD_MARGIN_BOTTOM, FIELD_GAP, FIELD_STYLE_GRID_GAP } from '../../../config/ui';
import {
  checkboxSx, checkboxIconSx, checkboxCheckedIconSx,
  expandedToggleButtonSx, expandedToggleButtonActiveSx, expandedSectionSx,
} from '../../../config/editorFieldStyles';
import { getValueByPath, isFieldEnabled } from '../../../utils/v2FieldOperations';
import { toV2VideoAssetReference } from '../../../utils/v2AssetUtils';
import { getFieldTestId, getSectionTestId, getStyleCheckboxTestId, getStyleInputTestId } from '../../../utils/testIdUtils';
import type { FlowStepNode } from '@modules/reactFlow/adapters/V2ScreenAdapter';
import type { OnboardingThemeSettingsV2 } from '@modules/firebase-core/converters/onboardingConverter';
import { getThemedMediaDefaults } from '../../../utils/v2ThemeDefaults';

export interface V2MediaSectionProps {
  step: FlowStepNode;
  locale: string;
  onChange: (path: string, value: unknown) => void;
  disabled?: boolean;
  defaultExpanded?: boolean;
  projectId?: string;
  onboardingId?: string;
  versionId?: string;
  availableLocales?: string[];
  /** Theme settings for themed defaults on media toggle ON */
  themeSettingsV2?: OnboardingThemeSettingsV2 | null;
}

export const V2MediaSection: React.FC<V2MediaSectionProps> = ({
  step,
  locale,
  onChange,
  disabled = false,
  defaultExpanded = true,
  projectId,
  onboardingId,
  versionId,
  themeSettingsV2,
}) => {
  const [uploading, setUploading] = useState(false);
  const [appearanceExpanded, setAppearanceExpanded] = useState(false);

  const uploadService = useMemo(() => new OnboardingMediaUploadService(), []);

  const isMediaEnabled = useMemo(
    () => isFieldEnabled(step, 'content.featuredMedia'),
    [step]
  );

  const mediaKind = useMemo<V2MediaKind>(() => {
    const kind = getValueByPath<string>(step, 'content.featuredMedia.kind');
    return (kind as V2MediaKind) ?? 'Image';
  }, [step]);

  // V2 uses translations for localized assets: content.featuredMedia.content.image.translations.{locale}
  const imageUrl = useMemo(() => {
    const asset = getValueByPath<Record<string, string>>(
      step,
      `content.featuredMedia.content.image.translations.${locale}`
    );
    if (typeof asset === 'string') return asset;
    if (asset && typeof asset === 'object') return asset.original ?? asset.origin ?? null;
    return null;
  }, [step, locale]);

  const videoUrl = useMemo(() => {
    const asset = getValueByPath<Record<string, string>>(
      step,
      `content.featuredMedia.content.video.translations.${locale}`
    );
    if (typeof asset === 'string') return asset;
    if (asset && typeof asset === 'object') return asset.original ?? asset.origin ?? null;
    return null;
  }, [step, locale]);

  const mediaPaddingValues = useMemo<PaddingValues>(() => {
    const insets = getValueByPath<Record<string, unknown>>(step, 'content.featuredMedia.layout.insets');
    return {
      paddingTop: typeof insets?.top === 'number' ? insets.top : 0,
      paddingRight: typeof insets?.end === 'number' ? insets.end : 0,
      paddingBottom: typeof insets?.bottom === 'number' ? insets.bottom : 0,
      paddingLeft: typeof insets?.start === 'number' ? insets.start : 0,
    };
  }, [step]);

  const isGradientEnabled = useMemo(() => {
    const gradient = getValueByPath(step, 'content.featuredMedia.appearance.gradient');
    return gradient !== null && gradient !== undefined;
  }, [step]);

  const gradientHeight = useMemo(() => {
    return getValueByPath<number>(step, 'content.featuredMedia.appearance.gradient.heightPercentage') ?? 50;
  }, [step]);

  const gradientColor = useMemo(() => {
    const rawColors = getValueByPath<string[] | Record<string, string>>(
      step,
      'content.featuredMedia.appearance.gradient.colors'
    );
    const colors = Array.isArray(rawColors) ? rawColors : rawColors ? Object.values(rawColors) : undefined;
    return colors?.[0] ?? '#000000';
  }, [step]);

  const mediaTopAlignment = useMemo(() => {
    const value = getValueByPath<string>(step, V2_MEDIA_TOP_ALIGNMENT_FIELD.path);
    const heightPercentage = getValueByPath<number>(
      step,
      'content.featuredMedia.appearance.dimensions.heightPercentage'
    );
    if (typeof heightPercentage === 'number' && heightPercentage > 0) {
      return 'navigationBar';
    }
    return value === 'top' ? 'top' : 'navigationBar';
  }, [step]);

  const isMediaTopAlignmentDisabled = useMemo(() => {
    const heightPercentage = getValueByPath<number>(
      step,
      'content.featuredMedia.appearance.dimensions.heightPercentage'
    );
    return typeof heightPercentage === 'number' && heightPercentage > 0;
  }, [step]);

  const handleMediaEnabledChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        // Use themed media defaults (scaleMode, cornerRadii) if available,
        // seeding from the template matching THIS screen's subtype.
        if (themeSettingsV2) {
          onChange('content.featuredMedia', getThemedMediaDefaults(themeSettingsV2, step.subtype));
        } else {
          onChange('content.featuredMedia', V2_DEFAULT_MEDIA_STRUCTURE);
        }
      } else {
        onChange('content.featuredMedia', null);
      }
    },
    [onChange, themeSettingsV2]
  );

  const handleKindChange = useCallback(
    (event: SelectChangeEvent) => {
      const newKind = event.target.value as V2MediaKind;
      const currentMedia = getValueByPath<Record<string, unknown>>(step, 'content.featuredMedia');
      onChange('content.featuredMedia', {
        ...currentMedia,
        kind: newKind,
        content: newKind === 'Image' ? V2_DEFAULT_IMAGE_CONTENT : V2_DEFAULT_VIDEO_CONTENT,
      });
    },
    [onChange, step]
  );

  const handleImageChange = useCallback(
    (value: string | null) => {
      if (value) {
        onChange(`content.featuredMedia.content.image.translations.${locale}`, {
          original: value,
          small: value,
        });
      } else {
        onChange(`content.featuredMedia.content.image.translations.${locale}`, null);
      }
    },
    [onChange, locale]
  );

  const handleImageUpload = useCallback(
    async (file: File): Promise<string> => {
      setUploading(true);
      try {
        // Editor invariant: the section only mounts inside an open onboarding
        // editor where all three ids are present.
        const result = await uploadService.uploadImage(file, {
          projectId: projectId!,
          onboardingId: onboardingId!,
          versionId: versionId!,
          locale,
        });
        onChange(`content.featuredMedia.content.image.translations.${locale}`, {
          original: result.assetUrl.origin,
          small: result.assetUrl.small,
        });
        return result.assetUrl.origin;
      } finally {
        setUploading(false);
      }
    },
    [uploadService, projectId, onboardingId, versionId, locale, onChange]
  );

  const handleVideoChange = useCallback(
    (value: string | null) => {
      if (value) {
        onChange(`content.featuredMedia.content.video.translations.${locale}`, {
          original: value,
        });
      } else {
        onChange(`content.featuredMedia.content.video.translations.${locale}`, null);
      }
    },
    [onChange, locale]
  );

  const handleVideoUpload = useCallback(
    async (file: File, onProgress?: (fraction: number) => void): Promise<string> => {
      setUploading(true);
      try {
        const result = await uploadService.uploadVideo(file, {
          projectId: projectId!,
          onboardingId: onboardingId!,
          versionId: versionId!,
          locale,
        }, { onProgress });
        onChange(
          `content.featuredMedia.content.video.translations.${locale}`,
          toV2VideoAssetReference(result.assetUrl)
        );
        return result.assetUrl.origin;
      } finally {
        setUploading(false);
      }
    },
    [uploadService, projectId, onboardingId, versionId, locale, onChange]
  );

  const handleMediaPaddingChange = useCallback(
    (path: string, value: number) => {
      const edgeInsetPath = path
        .replace('.paddingTop', '.top')
        .replace('.paddingRight', '.end')
        .replace('.paddingBottom', '.bottom')
        .replace('.paddingLeft', '.start');
      onChange(edgeInsetPath, value);
    },
    [onChange]
  );

  const handleGradientToggle = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange(
        'content.featuredMedia.appearance.gradient',
        event.target.checked ? V2_FLEXIBLE_GRADIENT_STRUCTURE : null
      );
    },
    [onChange]
  );

  const handleGradientHeightChange = useCallback(
    (_event: Event, value: number | number[]) => {
      const newHeight = Array.isArray(value) ? value[0] : value;
      onChange('content.featuredMedia.appearance.gradient.heightPercentage', newHeight);
    },
    [onChange]
  );

  const handleGradientColorChange = useCallback(
    (value: unknown) => {
      const color = String(value);
      const rawColors = getValueByPath<string[] | Record<string, string>>(
        step,
        'content.featuredMedia.appearance.gradient.colors'
      );
      const currentColors = Array.isArray(rawColors)
        ? rawColors
        : rawColors
          ? Object.values(rawColors)
          : ['#000000', '#00000000'];

      onChange('content.featuredMedia.appearance.gradient.colors', [
        color,
        currentColors[1] ?? '#00000000',
      ]);
    },
    [step, onChange]
  );

  const SECTION_NAME = 'Media';

  // Minimal FieldConfig for ImageUploadElement
  const imageFieldConfig = useMemo(() => ({
    path: 'content.featuredMedia.content.image',
    label: 'Image',
    type: 'imageUpload' as const,
    l10nPath: 'content.featuredMedia.content.image',
    guideKey: 'screenedit-media-image-upload',
  }), []);

  const videoFieldConfig = useMemo(() => ({
    path: 'content.featuredMedia.content.video',
    label: 'Video',
    type: 'videoUpload' as const,
    l10nPath: 'content.featuredMedia.content.video',
    guideKey: 'screenedit-media-video-upload',
  }), []);

  return (
    <SAccordion
      title="Media"
      collapsible
      defaultExpanded={defaultExpanded}
      sx={{ marginBottom: SECTION_MARGIN_BOTTOM }}
      data-testid={getSectionTestId(SECTION_NAME)}
    >
      <EditorGuideProvider>
        {/* Enable Media Checkbox */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: FIELD_GAP,
            mb: FIELD_MARGIN_BOTTOM,
          }}
        >
          <Checkbox
            checked={isMediaEnabled}
            onChange={handleMediaEnabledChange}
            disabled={disabled}
            size="small"
            sx={checkboxSx}
            icon={<Box sx={checkboxIconSx} />}
            checkedIcon={<Box sx={checkboxCheckedIconSx}>✓</Box>}
            data-testid="media-enable-checkbox"
          />
          <Typography variant="body2" color="text.secondary">
            Enable
          </Typography>
        </Box>

        {/* Kind Selector + Appearance toggle */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: FIELD_MARGIN_BOTTOM }}>
          <FormControl size="small" sx={{ flex: 1 }}>
            <Select
              value={mediaKind}
              onChange={handleKindChange}
              disabled={disabled || !isMediaEnabled}
              data-testid="media-kind-select"
            >
              {V2_MEDIA_KIND_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <IconButton
            size="small"
            onClick={() => setAppearanceExpanded(prev => !prev)}
            disabled={disabled || !isMediaEnabled}
            sx={appearanceExpanded ? expandedToggleButtonActiveSx : expandedToggleButtonSx}
            aria-label="Toggle media settings"
            data-testid="media-settings-toggle"
          >
            <TuneIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Appearance settings (scaleMode, height%, cornerRadius, repeat) */}
        <Collapse in={appearanceExpanded && isMediaEnabled} timeout="auto" unmountOnExit>
          <Box sx={expandedSectionSx}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: FIELD_STYLE_GRID_GAP }}>
              <StyleFieldRow
                config={withScreenEditorFieldGuide(V2_MEDIA_TOP_ALIGNMENT_FIELD, 'media')}
                value={mediaTopAlignment}
                onChange={(value) => onChange(V2_MEDIA_TOP_ALIGNMENT_FIELD.path, value)}
                disabled={disabled || isMediaTopAlignmentDisabled}
                inputTestId={getStyleInputTestId('media', V2_MEDIA_TOP_ALIGNMENT_FIELD.label)}
                checkboxTestId={getStyleCheckboxTestId('media', V2_MEDIA_TOP_ALIGNMENT_FIELD.label)}
              />
              {withScreenEditorFieldGuides(V2_MEDIA_APPEARANCE_FIELDS, 'media').map((fieldConfig) => (
                <StyleFieldRow
                  key={fieldConfig.path}
                  config={fieldConfig}
                  value={getValueByPath(step as unknown as Record<string, unknown>, fieldConfig.path)}
                  onChange={(value) => onChange(fieldConfig.path, value)}
                  disabled={disabled}
                  inputTestId={getStyleInputTestId('media', fieldConfig.label)}
                  checkboxTestId={getStyleCheckboxTestId('media', fieldConfig.label)}
                />
              ))}
              {mediaKind === 'Video' && (
                <StyleFieldRow
                  config={withScreenEditorFieldGuide(V2_MEDIA_REPEAT_FIELD, 'media')}
                  value={getValueByPath(step as unknown as Record<string, unknown>, V2_MEDIA_REPEAT_FIELD.path)}
                  onChange={(value) => onChange(V2_MEDIA_REPEAT_FIELD.path, value)}
                  disabled={disabled}
                  inputTestId={getStyleInputTestId('media', V2_MEDIA_REPEAT_FIELD.label)}
                  checkboxTestId={getStyleCheckboxTestId('media', V2_MEDIA_REPEAT_FIELD.label)}
                />
              )}
            </Box>
            <Divider sx={{ my: 2 }} />
            <PaddingElement
              basePath="content.featuredMedia.layout.insets"
              values={mediaPaddingValues}
              onChange={handleMediaPaddingChange}
              disabled={disabled}
              label="Padding"
              defaultExpanded={false}
              data-testid="media-padding-element"
            />
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
              Gradient
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: FIELD_GAP, mb: FIELD_MARGIN_BOTTOM }}>
              <Checkbox
                checked={isGradientEnabled}
                onChange={handleGradientToggle}
                disabled={disabled}
                size="small"
                sx={checkboxSx}
                icon={<Box sx={checkboxIconSx} />}
                checkedIcon={<Box sx={checkboxCheckedIconSx}>✓</Box>}
                inputProps={{ 'aria-label': 'Enable Gradient' }}
                data-testid="media-gradient-enable"
              />
              <Typography variant="body2" color="text.secondary">
                Enable Gradient
              </Typography>
            </Box>
            {isGradientEnabled && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">Height</Typography>
                    <Typography variant="caption" color="text.primary" fontWeight={500}>
                      {gradientHeight}%
                    </Typography>
                  </Box>
                  <Slider
                    value={gradientHeight}
                    onChange={handleGradientHeightChange}
                    min={0}
                    max={100}
                    step={5}
                    disabled={disabled}
                    size="small"
                    data-testid="media-gradient-height-slider"
                  />
                </Box>
                <StyleFieldRow
                  config={withScreenEditorFieldGuide(V2_FLEXIBLE_GRADIENT_COLOR_CONFIG, 'media')}
                  value={gradientColor}
                  onChange={handleGradientColorChange}
                  disabled={disabled}
                />
              </Box>
            )}
          </Box>
        </Collapse>

        {/* Image upload (when kind = 'Image') */}
        {mediaKind === 'Image' && (
          <ImageUploadElement
            fieldConfig={imageFieldConfig}
            value={imageUrl}
            templateValue={null}
            enabled={true}
            locale={locale}
            onChange={handleImageChange}
            onEnabledChange={() => {}}
            onUpload={handleImageUpload}
            disabled={disabled || uploading || !isMediaEnabled}
            hideCheckbox={true}
            expandedStyles={false}
            onStylesExpandToggle={() => {}}
            styleValues={{}}
            onStyleChange={() => {}}
            data-testid={getFieldTestId(SECTION_NAME, imageFieldConfig.label)}
          />
        )}

        {/* Video upload (when kind = 'Video') */}
        {mediaKind === 'Video' && (
          <VideoUploadElement
            fieldConfig={videoFieldConfig}
            value={videoUrl}
            templateValue={null}
            enabled={true}
            locale={locale}
            onChange={handleVideoChange}
            onEnabledChange={() => {}}
            onUpload={handleVideoUpload}
            disabled={disabled || uploading || !isMediaEnabled}
            hideCheckbox={true}
            expandedStyles={false}
            onStylesExpandToggle={() => {}}
            styleValues={{}}
            onStyleChange={() => {}}
            data-testid={getFieldTestId(SECTION_NAME, videoFieldConfig.label)}
          />
        )}
      </EditorGuideProvider>
    </SAccordion>
  );
};
